import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Radio,
} from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import CheckIcon from "@material-ui/icons/Check";
import { CustomTheme } from "../../../../types/customTheme";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";

export interface ChargebeeSubscription {
  id: string;
  customer_id?: string;
  status?: string;
  created_at?: number;
  billing_period?: number;
  billing_period_unit?: string;
  subscription_items?: Record<string, any>[];
  due_invoices_count?: number;
  due_since?: number;
  total_dues?: number;
  started_at?: number;
  activated_at?: number;
  updated_at?: number;
  current_term_start?: number;
  current_term_end?: number;
  next_billing_at?: number;
  created_from_ip?: string;
  currency_code?: string;
  auto_collection?: string;
  [key: string]: any;
}

interface Props {
  subscriptions: ChargebeeSubscription[];
  selectedSubscriptionId: string;
  expandedSubscriptionId: string;
  isFetching: boolean;
  onSelectSubscription: (subscriptionId: string) => void;
  onToggleExpanded: (subscriptionId: string) => void;
}

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    listWrapper: {
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },
    subscriptionCard: {
      borderRadius: 12,
      backgroundColor: theme.palette.background.entity_background,
      border: `1px solid ${theme.palette.divider}`,
      cursor: "pointer",
      overflow: "hidden",
      transition: "all 0.2s ease",
      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    },
    selectedCard: {
      border: `2px solid ${theme.palette.custom.green.main}`,
      boxShadow: "0 3px 10px rgba(0,0,0,0.14)",
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 18px",
      gap: 14,
    },
    leftHeader: {
      display: "flex",
      alignItems: "center",
      gap: 14,
      minWidth: 0,
      flex: 1,
    },
    mainInfo: {
      minWidth: 0,
      flex: 1,
    },
    planTitle: {
      fontSize: 20,
      fontWeight: 600,
      color: theme.palette.text.primary,
      marginBottom: 6,
    },
    metaLine: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      color: theme.palette.text.secondary,
      fontSize: 13,
    },
    dividerDot: {
      color: theme.palette.divider,
    },
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      borderRadius: 18,
      padding: "3px 10px",
      fontSize: 12,
      fontWeight: 600,
      color: "#fff",
      backgroundColor: theme.palette.custom.green.main,
    },
    lockedPill: {
      backgroundColor: theme.palette.custom.red.main,
    },
    autoPill: {
      display: "inline-flex",
      alignItems: "center",
      borderRadius: 18,
      padding: "3px 10px",
      fontSize: 12,
      fontWeight: 600,
      color: "#fff",
      backgroundColor: "#607D8B",
    },
    rightHeader: {
      display: "flex",
      alignItems: "center",
      gap: 14,
      flexShrink: 0,
    },
    priceText: {
      fontSize: 24,
      fontWeight: 700,
      color: theme.palette.text.primary,
      textAlign: "right",
      lineHeight: 1,
    },
    periodText: {
      fontSize: 14,
      fontWeight: 400,
      color: theme.palette.text.primary,
    },
    selectedCheck: {
      color: theme.palette.custom.green.main,
      fontSize: 24,
    },
    detailsContainer: {
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: "20px 82px 22px",
      backgroundColor: theme.palette.background.default,
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 30,
    },
    detailsTitle: {
      fontSize: 16,
      fontWeight: 600,
      marginBottom: 10,
      color: theme.palette.text.primary,
    },
    detailList: {
      margin: 0,
      paddingLeft: 20,
      color: theme.palette.text.primary,
    },
    detailListItem: {
      marginBottom: 8,
      fontSize: 14,
    },
    tableWrapper: {
      overflowX: "auto",
      marginTop: 10,
      gridColumn: "1 / span 2",
    },
    smallTableCell: {
      fontSize: 12,
      padding: "6px 8px",
      wordBreak: "break-word",
    },
  })
);

const ACTIVE_STATUSES = ["active", "in_trial", "non_renewing"];

const formatDate = (timestamp?: number) => {
  if (!timestamp) return "-";
  return new Date(timestamp * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (timestamp?: number) => {
  if (!timestamp) return "-";
  return new Date(timestamp * 1000).toLocaleString();
};

const formatValue = (value: any) => {
  if (value === undefined || value === null || value === "") return "-";

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const getBillingPeriod = (subscription: ChargebeeSubscription) => {
  const period = subscription.billing_period;
  const unit = subscription.billing_period_unit;

  if (!period && !unit) return "-";

  const formattedUnit = unit
    ? unit.charAt(0).toUpperCase() + unit.slice(1)
    : "";

  return `${period || ""} ${formattedUnit}`.trim();
};

const maskText = (text?: string) => {
  if (!text) return "-";

  if (text.length <= 12) {
    return text;
  }

  return `${text.slice(0, 8)}...${text.slice(-6)}`;
};

const getPlanTitle = (subscription: ChargebeeSubscription) => {
  const firstItem = subscription.subscription_items?.[0];

  return (
    firstItem?.item_price_id ||
    firstItem?.item_type ||
    subscription.plan_id ||
    "Chargebee Subscription"
  );
};

const getSubscriptionAmount = (subscription: ChargebeeSubscription) => {
  const items = subscription.subscription_items || [];

  const totalAmount = items.reduce((sum, item) => {
    const amount = Number(item.amount || 0);
    return sum + amount;
  }, 0);

  if (!totalAmount) {
    return "-";
  }

  return (totalAmount / 100).toFixed(2);
};

const getBillingUnit = (subscription: ChargebeeSubscription) => {
  const unit = subscription.billing_period_unit || "";

  if (!unit) return "";

  return `/ ${unit}`;
};

const getSubscriptionItemColumns = (items: Record<string, any>[]) => {
  const columns = new Set<string>();

  items.forEach((item) => {
    Object.keys(item || {}).forEach((key) => columns.add(key));
  });

  return Array.from(columns);
};

const ChargebeeSubscriptionList: React.FC<Props> = ({
  subscriptions,
  selectedSubscriptionId,
  expandedSubscriptionId,
  isFetching,
  onSelectSubscription,
  onToggleExpanded,
}) => {
  const classes = useStyles();

  const renderSubscriptionItemsTable = (items?: Record<string, any>[]) => {
    if (!items || items.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          No subscription items found.
        </Typography>
      );
    }

    const columns = getSubscriptionItemColumns(items);

    return (
      <Box className={classes.tableWrapper}>
        {/*
        <Typography className={classes.detailsTitle}>
          Subscription Items
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column} className={classes.smallTableCell}>
                  <strong>{column}</strong>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {items.map((item, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((column) => (
                  <TableCell key={column} className={classes.smallTableCell}>
                    {formatValue(item[column])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        */}
      </Box>
    );
  };

  const renderSubscriptionDetails = (subscription: ChargebeeSubscription) => (
    <Box className={classes.detailsContainer}>
      <Box>
        <Typography className={classes.detailsTitle}>Included Items</Typography>

        {subscription.subscription_items &&
        subscription.subscription_items.length > 0 ? (
          <ul className={classes.detailList}>
            {subscription.subscription_items.map((item, index) => (
              <li key={index} className={classes.detailListItem}>
                {formatValue(item.item_price_id || item.item_type)}{" "}
                {item.quantity ? `(Qty: ${item.quantity})` : ""} —{" "}
                {item.amount ? (Number(item.amount) / 100).toFixed(2) : "-"}
              </li>
            ))}
          </ul>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No included items found.
          </Typography>
        )}
      </Box>

      <Box>
        <Typography className={classes.detailsTitle}>
          Additional Billing Info
        </Typography>

        <ul className={classes.detailList}>
          <li className={classes.detailListItem}>
            Next Billing: {formatDate(subscription.next_billing_at)}
          </li>
          <li className={classes.detailListItem}>
            Overdue Invoices: {formatValue(subscription.due_invoices_count)}
          </li>
          <li className={classes.detailListItem}>
            Total Dues: {formatValue(subscription.total_dues)}
          </li>
          <li className={classes.detailListItem}>
            Due Since: {formatDateTime(subscription.due_since)}
          </li>
          <li className={classes.detailListItem}>
            Started At: {formatDateTime(subscription.started_at)}
          </li>
          <li className={classes.detailListItem}>
            Activated At: {formatDateTime(subscription.activated_at)}
          </li>
          <li className={classes.detailListItem}>
            Updated At: {formatDateTime(subscription.updated_at)}
          </li>
          <li className={classes.detailListItem}>
            Current Term Start: {formatDateTime(subscription.current_term_start)}
          </li>
          <li className={classes.detailListItem}>
            Current Term End: {formatDateTime(subscription.current_term_end)}
          </li>
          <li className={classes.detailListItem}>
            Created From IP: {formatValue(subscription.created_from_ip)}
          </li>
        </ul>
      </Box>

      {renderSubscriptionItemsTable(subscription.subscription_items)}
    </Box>
  );

  if (isFetching) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: 10 }}>
        <CircularProgress size={22} />
        <Typography variant="body2">Loading subscriptions...</Typography>
      </Box>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No subscriptions to display.
      </Typography>
    );
  }

  return (
    <Box className={classes.listWrapper}>
      {subscriptions.map((subscription) => {
        const isSelected = selectedSubscriptionId === subscription.id;
        const isExpanded = expandedSubscriptionId === subscription.id;

        const normalizedStatus = String(subscription.status || "")
          .trim()
          .toLowerCase();

        const isActiveStatus = ACTIVE_STATUSES.includes(normalizedStatus);

        return (
          <Box
            key={subscription.id}
            className={`${classes.subscriptionCard} ${
              isSelected ? classes.selectedCard : ""
            }`}
            onClick={() => onSelectSubscription(subscription.id)}
          >
            <Box className={classes.cardHeader}>
              <Box className={classes.leftHeader}>
                <Radio
                  checked={isSelected}
                  onChange={() => onSelectSubscription(subscription.id)}
                  color="primary"
                />

                <Box className={classes.mainInfo}>
                  <Typography className={classes.planTitle}>
                    {getPlanTitle(subscription)}
                  </Typography>

                  <Box className={classes.metaLine}>
                    <span>ID: {maskText(subscription.id)}</span>
                    <span className={classes.dividerDot}>|</span>
                    <span>Created: {formatDate(subscription.created_at)}</span>
                    <span className={classes.dividerDot}>|</span>
                    <span>Billing: {getBillingPeriod(subscription)}</span>
                    <span className={classes.dividerDot}>|</span>
                    <span
                      className={`${classes.statusPill} ${
                        isActiveStatus ? "" : classes.lockedPill
                      }`}
                    >
                      {formatValue(subscription.status)}
                    </span>
                    {subscription.auto_collection && (
                      <span className={classes.autoPill}>
                        Auto-Col {subscription.auto_collection}
                      </span>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box className={classes.rightHeader}>
                {isSelected && <CheckIcon className={classes.selectedCheck} />}

                <Box>
                  <Typography className={classes.priceText}>
                    {getSubscriptionAmount(subscription)}{" "}
                    <span className={classes.periodText}>
                      {getBillingUnit(subscription)}
                    </span>
                  </Typography>

                  <span
                    onClick={(event: React.MouseEvent<HTMLSpanElement>) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onToggleExpanded(subscription.id);
                    }}
                    onMouseDown={(event: React.MouseEvent<HTMLSpanElement>) => {
                      event.stopPropagation();
                    }}
                    style={{ display: "inline-block", marginTop: 6 }}
                  >
                    <ButtonCommon
                      variant="contained"
                      color="yellow"
                      onClick={() => {}}
                      style={{
                        minWidth: 140,
                        height: 32,
                        boxShadow: "none",
                      }}
                    >
                      {isExpanded ? (
                        <Box display="flex" alignItems="center">
                          Hide Details <ExpandLessIcon fontSize="small" />
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center">
                          More Details <ExpandMoreIcon fontSize="small" />
                        </Box>
                      )}
                    </ButtonCommon>
                  </span>
                </Box>
              </Box>
            </Box>

            <Collapse in={isExpanded}>
              {renderSubscriptionDetails(subscription)}
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default ChargebeeSubscriptionList;